/* global CollectJS */
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState, useRef } from "react";
import Address from "../../../components/Address";
import { useFormik } from "formik";
import * as Yup from "yup";
import AxiosInstance from "../../AxiosInstance";
import { useParams } from "react-router-dom";
import AchInfo from "../../../components/Plan/AchInfo";
import Grid from "@mui/material/Grid";
import showToast from "../../../components/Toast/Toster";
import { Typography } from "@mui/material";
import WhiteButton from "../../../components/Button/WhiteButton";
import BlueButton from "../../../components/Button/BlueButton";
import { useUserType } from "../../../contexts/UserTypeContext";
import { getItem } from "../../../utils/storage";
import InputText from "../../../components/InputFields/InputText";
import { getTokenizationKey } from "../../../plugins/ApiHandler";
import { loadCardScript } from "../../../plugins/helpers";
import { nmiCollectPosInlineStyleConfig } from "../../../plugins/collectJsNmiPosShared";

const ACH_CONTACT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddAchAccount = ({
  setIsOpen,
  isOpen,
  companyData,
  isBilling,
  vaultId,
  fetchData,
  CustomerId,
  onAccountAdded,
  Gateway,
}) => {
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const { CompanyUrl } = useParams();
  const [loading, setLoading] = useState(false);
  const [tokenizationKey, setTokenizationKey] = useState(null);
  const [collectJSInitialized, setCollectJSInitialized] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [achFieldsValid, setAchFieldsValid] = useState({
    checkname: false,
    checkaba: false,
    checkaccount: false,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const planFormikValuesRef = useRef(null);
  const planFormikRef = useRef(null);
  const addPaymentInProgressRef = useRef(false);
  const collectJsTimeoutRef = useRef(null);
  const requestSafetyTimeoutRef = useRef(null);
  // NMI ACH when gateway is NMI or when company has NMI tokenization key (e.g. ACH enabled with NMI)
  const isNmiAch =
    (Gateway || "").toString().toUpperCase() === "NMI" || !!tokenizationKey;

  const COLLECT_JS_CALLBACK_TIMEOUT_MS = 35000;
  const REQUEST_SAFETY_MS = 36000;

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/[^\d]/g, "");
    if (phoneNumber?.length < 1) {
      return phoneNumber;
    }
    if (phoneNumber?.length <= 3) {
      return `(${phoneNumber}`;
    }
    if (phoneNumber?.length <= 6) {
      return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(3)}`;
    }
    return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(
      3,
      6
    )} - ${phoneNumber?.slice(6, 10)}`;
  };

  const planFormik = useFormik({
    initialValues: {
      FirstName: "",
      LastName: "",
      Number: "",
      Email: "",
      checkname: "",
      checkaba: "",
      checkaccount: "",
      Address: "",
      City: "",
      State: "",
      Country: "",
      Zip: "",
    },

    validationSchema: Yup.object().shape({
      FirstName: Yup.string().required("First name is required"),
      LastName: Yup.string().required("Last name is required"),
      Number: Yup.string()
        .required("Phone Number is required")
        .test(
          "phone-ten-digits",
          "Enter a valid 10-digit phone number",
          (value) => (value || "").replace(/\D/g, "").length >= 10
        ),
      Email: Yup.string()
        .trim()
        .required("Email is required")
        .matches(ACH_CONTACT_EMAIL_RE, "Enter a valid email address"),
      // When NMI ACH, bank fields are in Collect.js iframes so not in form state
      checkname: isNmiAch ? Yup.string() : Yup.string().required("Account Holder Name is required"),
      checkaba: isNmiAch ? Yup.string() : Yup.string().required("Routing Number is required"),
      checkaccount: isNmiAch ? Yup.string() : Yup.string().required("Account Number is required"),
      Address: Yup.string().required("Address is required"),
      City: Yup.string().required("City is required"),
      State: Yup.string().required("State is required"),
      Country: Yup.string().required("Country is required"),
      Zip: Yup.string()
        .required("Zip code is required")
        .matches(/^\d{5}(-\d{4})?$/, "Enter a valid U.S. ZIP code (e.g., 33101 or 33101-1234)"),
    }),

    onSubmit: async (values) => {
      if (scriptLoading) {
        showToast.error("Payment form is still loading. Please wait.");
        return;
      }
      if (!isNmiAch) {
        showToast.error("ACH is only supported with NMI gateway. Please configure NMI with ACH enabled.");
        return;
      }
      // ACH uses Payment Core only: get token from Collect.js then POST add-customer-card
      if (typeof CollectJS === "undefined" || !collectJSInitialized) {
        showToast.error("Payment form is still loading. Please wait and try again.");
        return;
      }
      planFormikValuesRef.current = values;
      setLoading(true);
      if (collectJsTimeoutRef.current) clearTimeout(collectJsTimeoutRef.current);
      collectJsTimeoutRef.current = setTimeout(() => {
        collectJsTimeoutRef.current = null;
        setLoading(false);
        showToast.error("Payment form did not respond in time. Please check your bank details and try again.");
      }, COLLECT_JS_CALLBACK_TIMEOUT_MS);
      try {
        CollectJS.startPaymentRequest();
      } catch (err) {
        if (collectJsTimeoutRef.current) clearTimeout(collectJsTimeoutRef.current);
        collectJsTimeoutRef.current = null;
        showToast.error(err?.message || "Failed to start payment request");
        setLoading(false);
      }
    },
  });

  const postAddCustomerCardWithToken = async (paymentToken, displayAccountNumberFromCollect = "") => {
    if (collectJsTimeoutRef.current) {
      clearTimeout(collectJsTimeoutRef.current);
      collectJsTimeoutRef.current = null;
    }
    if (addPaymentInProgressRef.current) return;
    const values = planFormikValuesRef.current;
    if (!values) {
      showToast.error("Form data missing. Please try again.");
      setLoading(false);
      return;
    }
    addPaymentInProgressRef.current = true;
    if (requestSafetyTimeoutRef.current) clearTimeout(requestSafetyTimeoutRef.current);
    requestSafetyTimeoutRef.current = setTimeout(() => {
      requestSafetyTimeoutRef.current = null;
      setLoading(false);
      setIsOpen(false);
      addPaymentInProgressRef.current = false;
      showToast.error("Request took too long. Please check if the account was added and refresh the page.");
    }, REQUEST_SAFETY_MS);
    try {
      const payload = {
        payment_token: paymentToken,
        type: "ach",
        first_name: values?.FirstName,
        last_name: values?.LastName,
        email: values?.Email,
        address1: values?.Address,
        city: values?.City,
        state: values?.State,
        zip: values?.Zip,
        country: values?.Country,
        phone: values?.Number,
        company: CompanyUrl,
        CompanyId: getItem("CompanyId") || companyData?.CompanyId,
        CustomerId: CustomerId ?? "",
      };
      if (displayAccountNumberFromCollect) payload.displayAccountNumber = displayAccountNumberFromCollect;
      if (isBilling && vaultId) payload.customer_vault_id = vaultId;
      const res = await AxiosInstance.post("/v1/nmi/add-customer-card", payload, { timeout: 28000 });
      if (res?.data?.status === 200) {
        if (requestSafetyTimeoutRef.current) {
          clearTimeout(requestSafetyTimeoutRef.current);
          requestSafetyTimeoutRef.current = null;
        }
        showToast.success(res?.data?.data?.message || res?.data?.message || "Account added successfully.");
        setIsOpen(false);
        setLoading(false);
        addPaymentInProgressRef.current = false;
        if (fetchData) setTimeout(() => fetchData(), 0);
        if (onAccountAdded) setTimeout(() => onAccountAdded(), 100);
      } else {
        if (requestSafetyTimeoutRef.current) {
          clearTimeout(requestSafetyTimeoutRef.current);
          requestSafetyTimeoutRef.current = null;
        }
        setLoading(false);
        addPaymentInProgressRef.current = false;
        const msg = res?.data?.message || res?.data?.data?.error || res?.data?.data?.message || "Failed to add account.";
        showToast.error(msg);
      }
    } catch (error) {
      if (requestSafetyTimeoutRef.current) {
        clearTimeout(requestSafetyTimeoutRef.current);
        requestSafetyTimeoutRef.current = null;
      }
      const errorMessage = typeof error === "string" ? error : (error?.response?.data?.message || error?.response?.data?.data?.error || error?.response?.data?.data?.message || error?.response?.data?.error || error?.message || "We couldn't save your ACH account. Please try again in a moment.");
      setLoading(false);
      showToast.error(errorMessage);
    } finally {
      if (requestSafetyTimeoutRef.current) {
        clearTimeout(requestSafetyTimeoutRef.current);
        requestSafetyTimeoutRef.current = null;
      }
      setLoading(false);
      addPaymentInProgressRef.current = false;
    }
  };

  const [modalKey, setModalKey] = useState(0);
  const [customerData, setCustomerData] = useState(null);

  // Fetch customer data when modal opens
  const fetchCustomerData = async () => {
    if (!CustomerId) {
      return;
    }
    try {
      const res = await AxiosInstance.get(
        `/v1/customer/detail/${CustomerId}`
      );
      if (res?.data?.data) {
        setCustomerData(res.data.data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (isOpen) {
      planFormik.resetForm();
      setModalKey(prev => prev + 1);
      setCustomerData(null);
      setCollectJSInitialized(false);
      setTokenizationKey(null);
      setAchFieldsValid({
        checkname: false,
        checkaba: false,
        checkaccount: false,
      });
      setSubmitAttempted(false);
      addPaymentInProgressRef.current = false;
      if (collectJsTimeoutRef.current) {
        clearTimeout(collectJsTimeoutRef.current);
        collectJsTimeoutRef.current = null;
      }
      if (requestSafetyTimeoutRef.current) {
        clearTimeout(requestSafetyTimeoutRef.current);
        requestSafetyTimeoutRef.current = null;
      }
      if (CustomerId) fetchCustomerData();
    }
  }, [isOpen, CustomerId]);

  // Load NMI tokenization key when modal opens so we know if company has NMI ACH (even when primary gateway is not NMI)
  useEffect(() => {
    if (!isOpen) return;
    const companyId = getItem("CompanyId") || companyData?.CompanyId;
    if (!companyId) return;
    setScriptLoading(true);
    getTokenizationKey(companyId)
      .then((keyResponse) => {
        if (keyResponse?.PublicKey) {
          return loadCardScript(keyResponse.PublicKey).then(() => keyResponse.PublicKey);
        }
        return null;
      })
      .then((key) => setTokenizationKey(key || null))
      .catch(() => setTokenizationKey(null))
      .finally(() => setScriptLoading(false));
  }, [isOpen, companyData?.CompanyId]);

  // Configure CollectJS for ACH fields when script and key are ready
  useEffect(() => {
    if (!isOpen || !isNmiAch || !tokenizationKey || typeof CollectJS === "undefined") {
      setCollectJSInitialized(false);
      return;
    }
    try {
      ["checkname", "checkaba", "checkaccount"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = "";
      });
      CollectJS.configure({
        tokenizationKey,
        ...nmiCollectPosInlineStyleConfig,
        validationCallback: function (field, status) {
          if (
            field === "checkname" ||
            field === "checkaba" ||
            field === "checkaccount"
          ) {
            setAchFieldsValid((prev) => ({ ...prev, [field]: status }));
          }
        },
        fields: {
          checkname: { placeholder: "Account Holder Name" },
          checkaba: { placeholder: "Routing Number" },
          checkaccount: { placeholder: "Account Number" },
        },
        callback: function (response) {
          if (collectJsTimeoutRef.current) {
            clearTimeout(collectJsTimeoutRef.current);
            collectJsTimeoutRef.current = null;
          }
          const token = response?.token ?? response?.payment_token;
          // Collect.js returns masked ACH account in response.check (same idea as response.card for card number)
          const check = response?.check || {};
          const last4 = check.last_four ?? check.last4 ?? check.account_last_four;
          const displayAccountNumber =
            (check.account_number && String(check.account_number).trim()) ||
            (check.number && String(check.number).trim()) ||
            (check.account && String(check.account).trim()) ||
            (last4 ? `****${String(last4).slice(-4)}` : "");
          if (token) {
            postAddCustomerCardWithToken(token, displayAccountNumber);
          } else {
            setLoading(false);
            const msg = response?.message || response?.error?.message || "Invalid bank details. Please check and try again.";
            showToast.error(msg);
            // Show field-level validation so UI matches rest of app (red borders + messages)
            const formik = planFormikRef.current;
            if (formik) {
              formik.setFieldError("checkname", "Account Holder Name is required");
              formik.setFieldError("checkaba", "Routing Number is required");
              formik.setFieldError("checkaccount", "Account Number is required");
              formik.setTouched((t) => ({ ...t, checkname: true, checkaba: true, checkaccount: true }));
            }
          }
        },
      });
      setCollectJSInitialized(true);
    } catch (e) {
      setCollectJSInitialized(false);
    }
  }, [isOpen, isNmiAch, tokenizationKey]);

  // US States mapping for state name to code conversion
  const US_STATES = [
    { name: "Alabama", code: "AL" },
    { name: "Alaska", code: "AK" },
    { name: "Arizona", code: "AZ" },
    { name: "Arkansas", code: "AR" },
    { name: "California", code: "CA" },
    { name: "Colorado", code: "CO" },
    { name: "Connecticut", code: "CT" },
    { name: "Delaware", code: "DE" },
    { name: "District of Columbia", code: "DC" },
    { name: "Florida", code: "FL" },
    { name: "Georgia", code: "GA" },
    { name: "Hawaii", code: "HI" },
    { name: "Idaho", code: "ID" },
    { name: "Illinois", code: "IL" },
    { name: "Indiana", code: "IN" },
    { name: "Iowa", code: "IA" },
    { name: "Kansas", code: "KS" },
    { name: "Kentucky", code: "KY" },
    { name: "Louisiana", code: "LA" },
    { name: "Maine", code: "ME" },
    { name: "Maryland", code: "MD" },
    { name: "Massachusetts", code: "MA" },
    { name: "Michigan", code: "MI" },
    { name: "Minnesota", code: "MN" },
    { name: "Mississippi", code: "MS" },
    { name: "Missouri", code: "MO" },
    { name: "Montana", code: "MT" },
    { name: "Nebraska", code: "NE" },
    { name: "Nevada", code: "NV" },
    { name: "New Hampshire", code: "NH" },
    { name: "New Jersey", code: "NJ" },
    { name: "New Mexico", code: "NM" },
    { name: "New York", code: "NY" },
    { name: "North Carolina", code: "NC" },
    { name: "North Dakota", code: "ND" },
    { name: "Ohio", code: "OH" },
    { name: "Oklahoma", code: "OK" },
    { name: "Oregon", code: "OR" },
    { name: "Pennsylvania", code: "PA" },
    { name: "Rhode Island", code: "RI" },
    { name: "South Carolina", code: "SC" },
    { name: "South Dakota", code: "SD" },
    { name: "Tennessee", code: "TN" },
    { name: "Texas", code: "TX" },
    { name: "Utah", code: "UT" },
    { name: "Vermont", code: "VT" },
    { name: "Virginia", code: "VA" },
    { name: "Washington", code: "WA" },
    { name: "West Virginia", code: "WV" },
    { name: "Wisconsin", code: "WI" },
    { name: "Wyoming", code: "WY" },
  ];

  // Auto-fill user information and address fields when customer data is loaded
  useEffect(() => {
    if (customerData && isOpen) {
      // Auto-fill user information
      planFormik.setFieldValue("FirstName", customerData?.FirstName || "");
      planFormik.setFieldValue("LastName", customerData?.LastName || "");
      planFormik.setFieldValue("Email", customerData?.EmailAddress || "");
      
      // Format and set phone number
      if (customerData?.PhoneNumber) {
        const formattedPhone = formatPhoneNumber(customerData.PhoneNumber);
        planFormik.setFieldValue("Number", formattedPhone);
      }
      
      // Auto-fill account holder name from first and last name
      const fullName = `${customerData?.FirstName || ""} ${customerData?.LastName || ""}`.trim();
      if (fullName) {
        planFormik.setFieldValue("checkname", fullName);
      }

      const locationDetails = customerData?.locationDetails?.[0];
      if (locationDetails) {
        // Set address fields
        planFormik.setFieldValue("Address", locationDetails?.Address || "");
        planFormik.setFieldValue("City", locationDetails?.City || "");
        
        // Handle state - convert name to code if needed
        let stateValue = locationDetails?.State || "";
        if (stateValue) {
          // Check if it's already a state code (2 letters, case-insensitive)
          const isStateCode = /^[A-Za-z]{2}$/.test(stateValue);
          if (isStateCode) {
            // Convert to uppercase for consistency
            stateValue = stateValue.toUpperCase();
          } else {
            // It's a state name, find the code
            const stateObj = US_STATES.find(
              (s) => s.name.toLowerCase() === stateValue.toLowerCase()
            );
            if (stateObj) {
              stateValue = stateObj.code;
            }
          }
        }
        planFormik.setFieldValue("State", stateValue);
        planFormik.setFieldValue("Zip", locationDetails?.Zip || "");
        
        // Set country
        if (locationDetails?.Country) {
          planFormik.setFieldValue("Country", locationDetails.Country);
        }
      }
    }
  }, [customerData, isOpen]);

  // Keep ref updated so CollectJS callback can set Formik errors
  useEffect(() => {
    planFormikRef.current = planFormik;
  }, [planFormik]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "Country") {
      planFormik.setFieldValue(name, value);
    } else if (name === "Number") {
      // Format phone number
      const formattedPhone = formatPhoneNumber(value);
      planFormik.setFieldValue(name, formattedPhone);
    } else if (name === "FirstName" || name === "LastName") {
      // Update account holder name when first or last name changes
      planFormik.setFieldValue(name, value);
      const firstName = name === "FirstName" ? value : planFormik.values.FirstName;
      const lastName = name === "LastName" ? value : planFormik.values.LastName;
      const fullName = `${firstName || ""} ${lastName || ""}`.trim();
      if (fullName) {
        planFormik.setFieldValue("checkname", fullName);
      }
    } else {
      planFormik.setFieldValue(name, type === "checkbox" ? checked : value);
    }
  };

  const handleZipChange = (event) => {
    const { name, value } = event.target;
    const regex = /^[A-Za-z0-9]*$/;

    if (regex.test(value) || value === "") {
      planFormik.setFieldValue(name, value);
    }
  };

  const fv = planFormik.values;
  const achPhoneDigits = (fv.Number || "").replace(/\D/g, "");
  const achEmailTrim = (fv.Email || "").trim();
  const achBasicsComplete =
    (fv.FirstName || "").trim().length > 0 &&
    (fv.LastName || "").trim().length > 0 &&
    achPhoneDigits.length >= 10 &&
    achEmailTrim.length > 0 &&
    ACH_CONTACT_EMAIL_RE.test(achEmailTrim) &&
    (fv.Address || "").trim().length > 0 &&
    (fv.City || "").trim().length > 0 &&
    String(fv.State ?? "").trim().length > 0 &&
    (fv.Zip || "").trim().length > 0 &&
    String(fv.Country ?? "").trim().length > 0;

  const achCollectReady =
    achFieldsValid.checkname &&
    achFieldsValid.checkaba &&
    achFieldsValid.checkaccount;

  const canSubmitAchAccount =
    isNmiAch &&
    !scriptLoading &&
    collectJSInitialized &&
    achBasicsComplete &&
    achCollectReady;

  const handleAchDialogClose = () => {
    setIsOpen(false);
    planFormik.resetForm();
    setSubmitAttempted(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleAchDialogClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle
        className={`${
          isDonationMode ? "border-donor-color" : "border-orange-color"
        }`}
        style={{ borderBottom: "2px solid" }}
        sx={{ px: 2, pt: 2, pb: 1.5 }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          sx={{ gap: 1, width: "100%" }}
        >
          <Grid size="grow" sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              className={`text-lg cardInformationDetail ${
                isDonationMode ? "text-donor-color" : "text-blue-color"
              }`}
              style={{ fontWeight: "bold", fontSize: "18px" }}
            >
              User Information
            </Typography>
          </Grid>
          <Grid size="auto">
            <IconButton
              aria-label="close"
              size="small"
              onClick={handleAchDialogClose}
              sx={{
                color: isDonationMode
                  ? "var(--bg-donor-color)"
                  : "var(--text-blue-color)",
                "& .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent
        style={{ maxHeight: "calc(100vh - 150px)", paddingTop: 16 }}
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowY: "auto",
          scrollbarGutter: "stable",
          px: 2,
          pb: 2,
        }}
      >
        <Grid container spacing={2} sx={{ width: "100%", maxWidth: "100%", mt: 0, mx: 0 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputText
              value={planFormik?.values?.FirstName}
              onChange={planFormik?.handleChange}
              onBlur={planFormik?.handleBlur}
              error={
                planFormik?.touched?.FirstName &&
                Boolean(planFormik?.errors?.FirstName)
              }
              showErrorOnly
              name="FirstName"
              label="First Name"
              type="text"
              className="text-blue-color w-100"
              fieldHeight="44px"
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputText
              value={planFormik?.values?.LastName}
              onChange={planFormik?.handleChange}
              onBlur={planFormik?.handleBlur}
              error={
                planFormik?.touched?.LastName &&
                Boolean(planFormik?.errors?.LastName)
              }
              showErrorOnly
              name="LastName"
              label="Last Name"
              type="text"
              className="text-blue-color w-100"
              fieldHeight="44px"
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputText
              value={planFormik?.values?.Number}
              onChange={handleChange}
              onBlur={planFormik?.handleBlur}
              error={
                planFormik?.touched?.Number &&
                Boolean(planFormik?.errors?.Number)
              }
              showErrorOnly
              name="Number"
              label="Phone Number"
              type="text"
              inputProps={{ inputMode: "tel", autoComplete: "tel" }}
              className="text-blue-color w-100"
              fieldHeight="44px"
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputText
              value={planFormik?.values?.Email}
              onChange={planFormik?.handleChange}
              onBlur={planFormik?.handleBlur}
              error={
                planFormik?.touched?.Email && Boolean(planFormik?.errors?.Email)
              }
              showErrorOnly
              name="Email"
              label="Email"
              type="text"
              inputProps={{ autoComplete: "email" }}
              className="text-blue-color w-100"
              fieldHeight="44px"
              required
            />
          </Grid>
          <Grid size={12}>
            <Address
              key={modalKey}
              formik={planFormik}
              handleChange={handleChange}
              handleZipChange={handleZipChange}
            />
          </Grid>
          <Grid size={12}>
            <AchInfo formik={planFormik} useCollectJsInline={isNmiAch} />
          </Grid>
        {(() => {
          // Only show error message if form has been submitted OR at least one field with error has been touched
          const hasErrors = Object.keys(planFormik.errors || {}).length > 0;
          if (!hasErrors) return null;
          
          // Check if any field with an error has been touched
          const hasTouchedErrors = Object.keys(planFormik.errors || {}).some(
            (fieldName) => planFormik.touched[fieldName]
          );
          
          // Show error message only if form was submitted or at least one error field is touched
          if (submitAttempted || hasTouchedErrors) {
            return (
              <Grid size={12}>
              <Typography
                variant="body2"
                sx={{
                  color: "#dc3545",
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                Please correct the red boxes above
              </Typography>
              </Grid>
            );
          }
          return null;
        })()}
          <Grid
            size={12}
            className="flex-bottom BlueAndWhiteBtmFlex accountInformationBtn"
            sx={{
              mt: 0,
              pt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              width: "100%",
            }}
          >
            <WhiteButton
              type="button"
              label="Cancel"
              style={{ minHeight: 42 }}
              onClick={handleAchDialogClose}
            />
            <BlueButton
              type="button"
              style={{ minHeight: 42 }}
              onClick={() => {
                setSubmitAttempted(true);
                planFormik.setTouched({
                  FirstName: true,
                  LastName: true,
                  Number: true,
                  Email: true,
                  Address: true,
                  City: true,
                  State: true,
                  Country: true,
                  Zip: true,
                  checkname: true,
                  checkaba: true,
                  checkaccount: true,
                });
                planFormik.handleSubmit();
              }}
              isDonationMode={isDonationMode}
              disabled={loading || !canSubmitAchAccount}
              label={
                loading ? (
                  <CircularProgress size={20} style={{ color: "#fff" }} />
                ) : (
                  "Add Account"
                )
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AddAchAccount;
